<template>
    <div class="shipments-filters">
        <v-btn
            class="shipments-filters__reset-btn"
            outlined
            color="#967adc"
            @click="resetFilters"
        >
            {{ $t('pages.shipments.resetFilters') }}
        </v-btn>
        <div class="shipments-filters__actions">
            <div class="shipments-filters__filter">
                <v-text-field
                    v-model="localFilters.search"
                    dense
                    :label="$t('pages.shipments.search')"
                    hide-details
                    :placeholder="$t('pages.shipments.search')"
                    @input="onSearchInput"
                ></v-text-field>
            </div>
            <div class="shipments-filters__filter">
                <v-autocomplete
                    v-model="localFilters.status"
                    :items="statusItems"
                    dense
                    :label="$t('pages.shipments.status')"
                    hide-details
                ></v-autocomplete>
            </div>
            <div class="shipments-filters__filter">
                <v-autocomplete
                    v-model="localFilters.usmanager_id"
                    :items="usManagerItems"
                    :loading="isLoadingManagers"
                    dense
                    :label="$t('pages.shipments.usManager')"
                    hide-details
                    clearable
                    hide-no-data
                    :placeholder="$t('pages.report.any')"
                    @change="onUsManagerChange"
                ></v-autocomplete>
            </div>
            <div class="shipments-filters__filter">
                <v-text-field
                    v-model="localFilters.dateFrom"
                    dense
                    outlined
                    type="date"
                    name="dateFrom"
                    :label="$t('pages.report.from')"
                    hide-details
                ></v-text-field>
            </div>
            <div class="shipments-filters__filter">
                <v-text-field
                    v-model="localFilters.dateTo"
                    dense
                    outlined
                    type="date"
                    name="dateTo"
                    :label="$t('pages.report.to')"
                    hide-details
                ></v-text-field>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getterTypes } from '@/store/modules/shipments'
import {
    actionTypes as usersActionTypes,
    getterTypes as gettersTypesUsers,
} from '@/store/modules/users'
import languageMixin from '@/mixins/language.mixin'

export default {
    name: 'ShipmentsFilters',
    mixins: [languageMixin],
    props: {
        useShipmentItemsFilters: {
            type: Boolean,
            default: false,
        },
        customStatusItems: {
            type: Array,
            default: null,
        },
    },
    emits: ['filters-change', 'reset-filters'],
    data() {
        return {
            searchTimeout: null,
            isInitializing: true,
            localFilters: {
                status: '',
                search: '',
                dateFrom: '',
                dateTo: '',
                usmanager_id: null,
            },
        }
    },
    computed: {
        ...mapGetters('shipments', {
            shipmentFilters: getterTypes.getFilters,
            shipmentItemsFilters: getterTypes.getShipmentItemsFilters,
        }),
        ...mapGetters('users', {
            usersByRole: gettersTypesUsers.getUsersByRole,
            isFetching: gettersTypesUsers.isFetching,
        }),
        filters() {
            return this.useShipmentItemsFilters
                ? this.shipmentItemsFilters
                : this.shipmentFilters
        },
        isLoadingManagers() {
            return this.isFetching
        },
        usManagerItems() {
            if (!this.usersByRole || !Array.isArray(this.usersByRole)) {
                return []
            }
            return this.usersByRole.map((user) => ({
                text: user.nickName || user.nickname || `User ${user.id}`,
                value: Number(user.id),
            }))
        },
        defaultStatusItems() {
            return [
                {
                    text: this.$t('pages.report.any'),
                    value: '',
                },
                {
                    text: this.$t('pages.shipments.open'),
                    value: 'opened',
                },
                {
                    text: this.$t('pages.shipments.closed'),
                    value: 'closed',
                },
            ]
        },
        shipmentItemsStatusItems() {
            return [
                {
                    text: this.$t('pages.report.any'),
                    value: '',
                },
                {
                    text: this.$t('pages.shipments.open'),
                    value: 'open',
                },
            ]
        },
        statusItems() {
            if (this.customStatusItems && Array.isArray(this.customStatusItems)) {
                return this.customStatusItems
            }
            if (this.useShipmentItemsFilters) {
                return this.shipmentItemsStatusItems
            }
            return this.defaultStatusItems
        },
    },
    watch: {
        'localFilters.status': function () {
            if (!this.isInitializing) {
                this.$emit('filters-change', { ...this.localFilters })
            }
        },
        'localFilters.dateFrom': function () {
            if (!this.isInitializing) {
                this.$emit('filters-change', { ...this.localFilters })
            }
        },
        'localFilters.dateTo': function () {
            if (!this.isInitializing) {
                this.$emit('filters-change', { ...this.localFilters })
            }
        },
    },
    mounted() {
        const dateFrom = this.filters.dateFrom || ''
        const dateTo = this.filters.dateTo || ''
        
        let defaultStatus = this.filters.status || ''

        if (!defaultStatus && this.useShipmentItemsFilters) {
            defaultStatus = 'open'
        }

        if (!defaultStatus && !this.useShipmentItemsFilters) {
            defaultStatus = 'opened'
        }
        
        this.localFilters = {
            status: defaultStatus,
            search: this.filters.search || '',
            dateFrom,
            dateTo,
            usmanager_id: this.filters.usmanager_id || null,
        }
        
        if (!dateFrom && !dateTo) {
            this.presentDate()
        }
        
        this.$nextTick(() => {
            this.isInitializing = false
            this.$emit('filters-change', { ...this.localFilters })
        })

        this.loadUsManagers()
    },
    methods: {
        ...mapActions('users', {
            loadUsersByRole: usersActionTypes.loadUsersByRole,
        }),
        addZero: function (data) {
            return data < 10 ? '0' + data : data
        },
        presentDate: function () {
            const date = new Date()
            const year = date.getFullYear()
            const month = this.addZero(date.getMonth() + 1)
            const day = this.addZero(date.getDate())
            const today = `${year}-${month}-${day}`

            this.localFilters.dateFrom = '2026-01-01'
            this.localFilters.dateTo = today
        },
        onSearchInput: function () {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            this.searchTimeout = setTimeout(() => {
                this.$emit('filters-change', { ...this.localFilters })
            }, 500)
        },
        resetFilters: function () {
            this.isInitializing = true
            
            const defaultStatus = this.useShipmentItemsFilters ? 'open' : 'opened'
            
            this.localFilters = {
                status: defaultStatus,
                search: '',
                dateFrom: '',
                dateTo: '',
                usmanager_id: null,
            }

            this.presentDate()
            
            this.$nextTick(() => {
                this.isInitializing = false
                this.$emit('reset-filters')
            })
        },
        loadUsManagers() {
            this.loadUsersByRole('USA_MANAGER')
        },
        onUsManagerChange() {
            if (this.isInitializing) return

            const filters = { ...this.localFilters }

            if (filters.usmanager_id) {
                filters.usmanager_id = Number(filters.usmanager_id)
            }

            if (!filters.usmanager_id) {
                filters.usmanager_id = null
            }

            this.$emit('filters-change', filters)
        },
    },
}
</script>

<style lang="scss" scoped>
.shipments-filters {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;

    &__reset-btn {
        width: 100%;
        height: 40px;
        align-self: flex-start;
        margin-left: 0;

        @include tablet-up {
            max-width: 130px;
            width: auto;
        }

        @include for-hebrew {
            margin-left: 0;
            margin-right: 0;

            @include tablet-up {
                margin-right: 0;
            }
        }
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: flex-end;
    }

    &__filter {
        width: 100%;
        max-width: 100%;
        min-width: 100%;

        @include tablet-up {
            max-width: 200px;
            min-width: 150px;
        }
    }
}
</style>
